// oxlint-disable react/only-export-components -- provider e hook de contexto
// vivem juntos por convenção; separá-los só para o fast refresh não compensa.
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { sessao as guardaToken } from '../api/client'
import type { Sessao } from '../types/api'

interface Contexto {
  autenticado: boolean
  empresa: Sessao['empresa'] | null
  entrar: (s: Sessao) => void
  sair: () => void
}

const SessaoContext = createContext<Contexto | null>(null)

const CHAVE_EMPRESA = 'polo.empresa'

function lerEmpresa(): Sessao['empresa'] | null {
  try {
    const cru = localStorage.getItem(CHAVE_EMPRESA)
    return cru ? (JSON.parse(cru) as Sessao['empresa']) : null
  } catch {
    return null
  }
}

export function SessaoProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Inicializador preguiçoso: lê uma vez na montagem, sem efeito e sem
  // renderizar duas vezes.
  const [empresa, setEmpresa] = useState<Sessao['empresa'] | null>(() =>
    guardaToken.ler() ? lerEmpresa() : null,
  )

  const entrar = useCallback(
    (s: Sessao) => {
      guardaToken.gravar(s.token)
      try {
        localStorage.setItem(CHAVE_EMPRESA, JSON.stringify(s.empresa))
      } catch {
        // Sem persistência a sessão dura só esta aba; o token já está em memória.
      }
      setEmpresa(s.empresa)
    },
    [],
  )

  const sair = useCallback(() => {
    guardaToken.limpar()
    try {
      localStorage.removeItem(CHAVE_EMPRESA)
    } catch {
      // Ignorado de propósito.
    }
    setEmpresa(null)
    // Sem isto os dados da empresa anterior ficariam em cache para o próximo login.
    queryClient.removeQueries({ queryKey: ['minha-empresa'] })
  }, [queryClient])

  const valor = useMemo<Contexto>(
    () => ({ autenticado: empresa !== null, empresa, entrar, sair }),
    [empresa, entrar, sair],
  )

  return <SessaoContext value={valor}>{children}</SessaoContext>
}

export function useSessao(): Contexto {
  const ctx = use(SessaoContext)
  if (ctx === null) throw new Error('useSessao precisa estar dentro de SessaoProvider')
  return ctx
}
